import { Invoice, InvoiceLine, InvoiceLineAllocation, ServiceItem, ServiceJournalKind, ServiceTransaction } from '../types';
import { allocatableEntries, allocatedByEntry, ohneVerzichteteEintraege, planAllocations } from './allocationLogic';
import { zahl, zeitraumMengen } from './allocationShadow';
import { computeCumulativePositions, computeOpenPositions, undatedBacklog } from './invoiceLogic';
import { stampFlatCoverage } from './projectLv';
import {
    ServiceJournalRow,
    isJournalMappable,
    journalToServiceTransactions,
    kennungFuerVergleich,
    loadJournal,
} from './serviceJournal';
import { asDay, buchungsartVon, buchungstag, isAccountingEntry } from './serviceLogUtils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DER SCHATTEN AM TOR (S5a Stufe 2, erweitert in Stufe 3a) — OHNE WIRKUNG
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * S5a stellt die Zuordnung (`allocations.entryId`: welche erbrachte Menge
 * welchem Beleg berechnet ist) vom Projekt-JSON auf das Buchungsjournal um.
 * Der naechtliche Schatten (allocationShadow.ts) misst das seit Wochen — aber
 * OHNE Zeitraum und ohne echte Zeilenmengen: einen Belegzeitraum und einen
 * kumulativen Zuwachs gibt es erst beim Ausstellen. Genau dort wird Stufe 3
 * entscheiden, und genau dort misst dieses Modul.
 *
 * Beim Ausstellen rechnet `planProjectIssue` (invoiceService) die Zuordnung
 * wie bisher aus dem JSON — MASSGEBLICH, unveraendert — und ruft danach diesen
 * Schatten, OHNE auf ihn zu warten. Er rechnet dieselbe Zuordnung noch einmal
 * aus dem Journal, mit GENAU denselben uebrigen Eingaben (Zeilen, Vorbelege,
 * Zeitraum, Zuwachsfunktion), vergleicht beide und meldet eine Abweichung nach
 * `bug_reports`. Der Beleg erfaehrt davon nichts.
 *
 * Betreiber-Entscheide vom 02.10.2026:
 *   1. Das Zuordnungsdatum ist der BERLINER Tag. Verglichen wird deshalb auf
 *      beiden Seiten ueber `buchungstag` (dieselbe Regel wie `entry_day`).
 *   2. Gesperrt wird ab Stufe 3b — aber NICHT hier: das Tor
 *      (allocationGate.ts) nimmt die reinen Teile dieses Moduls
 *      (`schnappschussAmTor`, `journalwegAmTor`, `vergleicheTorZuordnung`)
 *      und entscheidet selbst. Der Schatten `schattenAmTor` sperrt NIE — er
 *      hat dafuer auch keinen Weg: er gibt nichts zurueck, was der Aufrufer
 *      liest, und wirft nie. Er laeuft seit 3b nur noch im JSON-Modus
 *      (Tor-Notschalter AN) und fuer Belege ohne Leistungszeile mit
 *      positiver Menge (Storno, Gutschrift, Betragskorrektur — `SchattenAnlass`).
 *   3. Ist das Journal nicht lesbar, meldet der Schatten nur; das Tor sperrt.
 *
 * ─── Warum hier nichts nachgebaut wird ─────────────────────────────────────
 * Dieselbe Begruendung wie im Nachtlauf: der Journal-Weg ist
 * `journalToServiceTransactions` (der Leser, den `getServiceLog` spaeter
 * nimmt) und danach `planAllocations` selbst. Der Zeitraumdeckel ist
 * `loggedQuantityInPeriod` selbst (ueber `zeitraumMengen`), die Groessen des
 * Entwurfs sind `computeOpenPositions`, `computeCumulativePositions` und
 * `undatedBacklog` selbst. Eine Nachbildung maesse sich selbst. Aus dem
 * Journal gelesene Zeilen werden NIE zurueckgespiegelt (Begruendung an
 * `journalIdDecision`) — dieses Modul schreibt ohnehin nur nach `bug_reports`.
 *
 * ─── Was als Abweichung zaehlt ─────────────────────────────────────────────
 * Je Belegzeile die Zuordnungen `{entryId, quantity, date}`:
 *   - ein Eintrag nur auf einer Seite (`eintrag_fehlt_im_journal`,
 *     `eintrag_nur_im_journal`),
 *   - eine andere Menge (`menge_weicht_ab`, EPS 0.0005 wie allocationLogic),
 *   - ein anderer BERLINER Tag (`tag_weicht_ab`). Seit dem 03.10.2026
 *     (fdf83f3) friert `planAllocations` auf BEIDEN Wegen den Berliner Tag ein
 *     (`buchungstag`, Entscheid 1). Normalisiert wird trotzdem weiter ueber
 *     das DATUM DER PROTOKOLLZEILE — es schadet nicht und haelt den Vergleich
 *     richtig, falls ein Weg je wieder anders schneidet.
 * Dazu je Leistung der Belegzeilen der Zeitraumdeckel im Belegzeitraum
 * (`zeitraum_menge_weicht_ab`): die im Zeitraum noch OFFENE Menge, mit den
 * Vorbelegen als Abzug — genau der Deckel, den `computeOpenPositions` beim
 * Anlegen des Entwurfs zieht. Beide Seiten auf dem Berliner Tag.
 *
 * Und seit Stufe 3a je Leistung der abzurechnenden Belegzeilen die drei
 * Groessen, die bis dahin am Tor niemand verglich (Abschnitt „Stufe 3a"):
 *   - der Stand bis zum Stichtag (`stichtagsmenge_weicht_ab`),
 *   - die Menge ohne Datum (`undatiert_weicht_ab`),
 *   - je Eintrag die Buchungsart (`buchungsart_weicht_ab`).
 * Jede davon nur, wenn sie Geld bewegt (Geldprobe, unten).
 *
 * ─── Was NICHT als Abweichung zaehlt ───────────────────────────────────────
 * Eine Zeile, deren Zuordnung auf beiden Seiten zeichengleich ist, wird gar
 * nicht erst verglichen: beide Wege froeren dasselbe ein. Das betrifft vor
 * allem die Zeilen, die `planAllocations` durchreicht (Storno, Gutschrift,
 * reine Betragskorrektur) — ihre mitgebrachte Zuordnung durch zwei
 * verschiedene Protokolle zu deuten, erzeugte Befunde ohne Unterschied.
 *
 * Und der GLEICHSTAND: zwei Eintraege derselben Leistung am selben Berliner
 * Tag. Welcher zuerst gezogen wird, entscheidet `orderForPeriod` nach der
 * Berliner Wanduhr und bei gleichem Zeitpunkt nach der id — seit dem
 * 03.10.2026 auf beiden Wegen gleich; vorher entschied die Arrayposition
 * (JSON) bzw. die Reihenfolge, in der das Journal zurueckkommt. Getauscht
 * werden kann trotzdem noch, wenn derselbe Eintrag auf den beiden Seiten
 * verschiedene Uhrzeiten desselben Tages traegt (reiner Tag im JSON,
 * `entry_at` mit Uhrzeit im Journal). Der Nachtlauf hat gelernt,
 * was ein Vergleich daraus macht (1141 Fehlalarme in der ersten Nacht).
 * Getauscht gilt deshalb NUR, wenn ALLE beteiligten Eintraege auf beiden
 * Seiten abrechenbar sind, auf beiden dieselbe erbrachte Menge und denselben
 * Berliner Tag tragen und die zugeordnete Summe dieses Tages gleich ist —
 * dann ist es dasselbe Geld im selben Monat, nur ein anderer Eintrag. Das
 * wird GEZAEHLT (`getauscht`) und steht in jeder Meldung, erzeugt aber
 * allein keine. Fehlt einer der Eintraege auf einer Seite, ist es kein
 * Gleichstand, sondern ein Befund.
 *
 * Aus demselben Grund zaehlt ein Unterschied in einer der Groessen von
 * Stufe 3a, der keine Menge bewegt, nicht als Befund: er wird GEZAEHLT
 * (`ohneGeldwirkung`, je Groesse) und steht in jeder Meldung wie `getauscht`.
 *
 * ─── Stufe 3a: was das Protokoll ausserdem an Mengen bewegt ────────────────
 * Die Zuordnung am Tor liest aus dem Protokoll nur, WELCHE Eintraege offen
 * sind. Die MENGE der Belegzeilen entsteht frueher, beim Aufbau des Entwurfs
 * (buildProjectServiceLines), und dort wirkt das Protokoll an drei weiteren
 * Stellen (Notiz 03.10.2026). Solange der Entwurf aus dem JSON gebaut wurde,
 * kamen sie am Tor gar nicht vor; liest er das Journal, bewegen sie Geld.
 * Seit Stufe 3c tut er das im Journal-Modus (entwurfAusJournal.ts, mit einem
 * Rueckschatten ueber das JSON beim Aufbau). Gemessen wird hier deshalb
 * weiter, und seit 3c ist es die Gegenprobe beim AUSSTELLEN: was der Entwurf
 * aus einem nachlaufenden Journal zu klein gebaut hat, steht am Tor als
 * Abweichung des Zeitraum- bzw. Stichtagsdeckels — mit den Funktionen des Entwurfs selbst,
 * auf beiden Protokollen, mit demselben LV (`services` des Tors, gestempelt
 * wie in getNormalizedServices: eine Pauschalposition steht still), demselben
 * Zeitraum und denselben Vorbelegen:
 *
 *   1. Der Stichtagsdeckel (`performedUpToCutoff`): was BIS zum Stichtag
 *      erbracht ist, eine ALT-Zeile nur, soweit sie Leistung traegt, die das
 *      eigene Protokoll nicht erklaert (6290-1). Kumulativ IST das die Menge
 *      der Zeile, fortschreibend ist es der dritte Deckel von
 *      `computeOpenPositions`. Gelesen ueber `computeCumulativePositions` ohne
 *      undatierte Mengen — also `min(quantityUsed, Stand)` —, nur mit
 *      Stichtag. Hier wirkt auch die Buchungsart: ob eine Zeile ALT ist,
 *      sagt links Register oder Praefix, rechts `kind = 'legacy_backfill'`.
 *   2. Die Menge ohne Datum (`includeUndated`): LV-Stand ohne
 *      Protokolleintrag (`undatedBacklog`). Ob der Entwurf sie mitnahm, steht
 *      seit dem 18.10.2026 am Beleg (`details.includeUndated`, Betreiber-
 *      Entscheid): jeder Aufbau der Zeilen aus dem Protokoll schreibt die
 *      Wahl, mit der er lief, jeder Neuaufbau ueberschreibt sie, eine von
 *      Hand geaenderte Menge laesst sie stehen. Gelesen wird sie im
 *      synchronen Teil von `schattenAmTor` und geht als
 *      `TorRahmen.includeUndated` in den Vergleich:
 *        - `true`: der Entwurf wurde mit „ohne Datum mitnehmen" gebaut.
 *          Verglichen wird die Luecke und als Geldprobe der Entwurf MIT ihr.
 *        - `false`: die Luecke floss nicht in den Entwurf. Ein Unterschied
 *          wird nur gezaehlt (`ohneGeldwirkung.undatiert`), nie gemeldet —
 *          Begruendung bei der Geldprobe unten.
 *        - fehlt (Entwurf aus der Zeit davor, oder kein Beleg am Eingang):
 *          wie bis zum 18.10. konservativ wie `true`, der Text sagt, dass die
 *          Wahl am Beleg nicht gespeichert ist.
 *      Fuer Stufe 3b: SPERREN darf `undatiert_weicht_ab` nur bei `true`. Ohne
 *      Angabe meldet der Schatten weiter, aber der Entwurf kann ebenso gut
 *      ohne die Option gebaut sein — eine Sperre traefe dann einen Beleg, den
 *      die Luecke gar nicht beruehrt.
 *      Hier liegt der Fall, den das Journal grundsaetzlich nicht abbilden
 *      kann: eine JSON-Zeile ohne Datum oder ohne lesbare Menge zaehlt im
 *      JSON als protokolliert und mindert die Luecke, faellt aber durch jeden
 *      Zeitraum — ein Entwurf mit Zeitraum bietet sie links NIE an. Im
 *      Journal gibt es sie nicht; ihre Menge steht dort IN der Luecke und
 *      wird mit „ohne Datum mitnehmen" angeboten. Gemessen wird deshalb nicht
 *      die Zeile, sondern was sie bewegt (Luecke und Entwurfsmenge); Zahl und
 *      Menge solcher Zeilen der Leistung stehen im Text.
 *   3. Die Buchungsart: „Buchung oder Arbeit?" entscheidet der JSON-Weg mit
 *      `isPostingEntry` — nach der Art am Eintrag, sonst aus dem
 *      Buchungsart-Register, sonst nach Beleg und Text. Das Register ist nur
 *      gefuellt, wenn das Projekt in DIESEM Tab geoeffnet wurde; im
 *      Rechnungslauf oder bei einem nie geoeffneten Projekt entscheidet links
 *      der Rueckfall, rechts immer die Spalte `kind`. Der Zuordnungsvergleich
 *      oben sieht davon nur, was gerade gezogen wird. Verglichen wird deshalb
 *      je Eintrag der Leistungen des Belegs, ob er auf beiden Seiten
 *      abrechenbar ist.
 *
 * Die GELDPROBE — gemeldet wird nur, was eine Menge bewegt:
 *   - Stichtag: nur, wenn auch die Entwurfsmenge ohne undatierte Mengen
 *     abweicht. Kumulativ ist sie der Stand selbst; fortschreibend greift der
 *     Deckel oft nicht, weil der Zeitraum oder der offene Bestand enger ist.
 *   - Ohne Datum: nur, wenn der Entwurf MIT undatierten Mengen abweicht. Ein
 *     Eintrag, der dem Journal nur nachlaeuft, schiebt rechts Menge aus dem
 *     Zeitraum in die Luecke; mit undatierten Mengen gleicht sich das aus,
 *     und gemeldet ist er dann bereits als Zeitraum- und Stichtagsabweichung.
 *     Und nur, wenn der Entwurf mit der Option gebaut wurde oder die Wahl
 *     unbekannt ist. Bei `false` kann die Luecke kein Geld bewegen: ohne die
 *     Option geht sie in `computeOpenPositions` und
 *     `computeCumulativePositions` an KEINER Stelle ein (beide lesen
 *     `undatedQuantities` nur mit ihr). Was ein anderes Protokoll dann
 *     noch an der Entwurfsmenge aendern kann, laeuft ueber genau zwei Wege,
 *     und beide sind schon abgedeckt: der Zeitraumdeckel (wird ohne
 *     Geldprobe verglichen, `zeitraum_menge_weicht_ab`) und der
 *     Stichtagsdeckel `performedUpToCutoff` (Geldprobe mit der Entwurfsmenge
 *     OHNE undatierte Mengen — das IST der Entwurf bei `false`;
 *     `stichtagsmenge_weicht_ab`, kumulativ ist es der Stand selbst). Der
 *     offene Bestand aus dem LV haengt an keinem Protokoll.
 *   - Buchungsart: nur, wenn auf der abrechenbaren Seite nach den Vorbelegen
 *     noch etwas offen ist UND die Position nicht durch eine Monatspauschale
 *     gedeckt ist (`flatCoveredBy` im gestempelten LV). Eine gedeckte
 *     Position steht mit ihrem Geldstand still — kumulativ ist ihr Zuwachs 0,
 *     fortschreibend ihre offene Menge —, ihre Eintraege ordnet kein Weg zu.
 *     Fuer Stichtag und Menge ohne Datum braucht es dafuer keine eigene
 *     Regel: `openQuantity` und `computeCumulativePositions` rechnen die
 *     gedeckte Position ohne Protokoll, beide Seiten sind gleich.
 *     Ohne LV kennt der Vergleich keine Deckung und prueft wie vorher.
 * Die Entwurfsmenge wird OHNE die Reservierungen anderer Entwuerfe gerechnet
 * (das Tor laedt sie nicht). Sie haengen nicht am Protokoll und sind auf
 * beiden Seiten gleich; ohne sie kann die Probe einen Unterschied nur
 * zusaetzlich zeigen, nie verdecken.
 *
 * Der SCHNAPPSCHUSS: das Register ist global und veraenderlich — zwischen
 * dem Ausstellen und der Ankunft des Journals kann ein Tab ein Projekt
 * oeffnen oder schliessen. Die JSON-Seite wird deshalb ganz im synchronen
 * Teil von `schattenAmTor` vermessen (`vermesseSeite`): Abrechenbarkeit, Art,
 * Zeitraumdeckel und Entwurfsgroessen, in dem Moment, in dem auch
 * `planAllocations` sie sah. Bis Stufe 2 las der Vergleich die
 * Abrechenbarkeit der JSON-Seite erst nach dem Journal, mit dem Register von
 * dann. Die Journal-Seite braucht das nicht: ihre Zeilen tragen `kind`
 * selbst, und `buchungsartVon` fragt das Register dann nie.
 *
 * ─── Wo die beiden Wege strukturell nicht vergleichbar sind ────────────────
 * - Zeitraumkante: seit dem 03.10.2026 entscheiden beide Wege "im
 *   Zeitraum?" nach dem Berliner Tag (`orderForPeriod`, fdf83f3). Eine
 *   Abweichung an der Kante waere also ein echter Befund; der Text nennt
 *   dann weiter beide Tage ("Tagesgrenze").
 * - Nicht abbildbare JSON-Zeilen (ohne Datum, ohne Leistung): der JSON-Weg
 *   zieht sie (undatiert ans Ende), im Journal koennen sie nie stehen. Als
 *   Zuordnung erscheinen sie als `eintrag_fehlt_im_journal` mit diesem
 *   Grund; was sie an Mengen bewegen, misst `undatiert_weicht_ab` (oben).
 * - Eine uuid in Grossbuchstaben im JSON kommt aus der uuid-Spalte klein
 *   zurueck. Seit dem 03.10.2026 (2c1d1f4) vergleichen `allocatedByEntry`
 *   und alle Suchen ueber `kennungFuerVergleich` — die Vorbelege werden auf
 *   beiden Wegen gefunden. Faellt es doch auseinander, wird es gemeldet. Die
 *   Buchungsart wird ueber dieselbe Kennung verglichen.
 * - Das Journal laeuft dem JSON nach (Doppelschreiben, Korb). Eine Leistung,
 *   die Minuten vor dem Ausstellen gebucht wurde, kann rechts noch fehlen.
 * - Das Journal wird NACH dem JSON gelesen; ein Schreibvorgang dazwischen
 *   steht nur rechts.
 *
 * ─── Was am Tor weiterhin NICHT verglichen wird, und warum ─────────────────
 * - Leistungen OHNE Zeile auf dem Beleg: was der Entwurf aus dem Journal
 *   zusaetzlich anboete, sieht erst das Tor des Belegs, der sie enthaelt.
 *   Alle Leistungen des Projekts zu vergleichen, meldete ausgeschlossene und
 *   von Hand entfernte Positionen mit.
 * - Storno, Gutschrift und reine Betragskorrektur fuer Stufe 3a: ihre Mengen
 *   stammen nicht aus dem Entwurfsaufbau, sondern aus dem Ursprungsbeleg.
 * - Die Zuwachsfunktion (kumulativ, bei einer Teilrechnung der Stand je
 *   Teilnehmer aus `billedByParty`): sie geht als Abschrift des JSON-Wegs in
 *   beide Rechnungen. Verglichen wird, was sie zuordnet, nicht sie selbst.
 * - Der Leistungszeitraum des Belegs (`performedSpanFromLog`) und die
 *   Riegel nach der Zuordnung lesen weiter das JSON; sie gehoeren nicht zur
 *   Zuordnung. Seit Stufe 3c rechnet sie ein eigener Schatten zusaetzlich
 *   auf dem Journal nach und meldet nur (torLeserSchatten.ts); umgeschaltet
 *   werden sie erst in Stufe 3d.
 *
 * Rein bis auf die Huelle `schattenAmTor`: `vergleicheTorZuordnung` hat kein
 * Netz, keine Uhr, keinen Zufall. Eine Ausnahme mit Absicht: ohne
 * `jsonSeite` vermisst sie die JSON-Seite selbst und liest dabei die
 * Buchungsart aus dem Register — am Tor kommt die Seite deshalb fertig aus
 * dem Schnappschuss.
 */

/** Wie in allocationLogic: darunter ist eine Menge Rundungsrauschen. */
const EPS = 0.0005;

/** Trennzeichen des Schluessels (Leistung, Eintrag) — Begruendung in allocationShadow. */
const TRENNER = '\u0000';

const schluessel = (serviceId: string, entryId: string): string => `${serviceId}${TRENNER}${entryId}`;

/**
 * Weichen zwei Mengen ab? NaN zaehlt als Abweichung: eine krumme Menge im JSON
 * rechnet der Entwurf mit — sie zu verschweigen hiesse, genau den Fall zu
 * uebersehen, den das Journal anders rechnet.
 */
const weichtAb = (x: number, y: number): boolean => !(Math.abs(x - y) <= EPS);

export type TorDiffArt =
    | 'eintrag_fehlt_im_journal'
    | 'eintrag_nur_im_journal'
    | 'menge_weicht_ab'
    | 'tag_weicht_ab'
    | 'zeitraum_menge_weicht_ab'
    | 'stichtagsmenge_weicht_ab'
    | 'undatiert_weicht_ab'
    | 'buchungsart_weicht_ab'
    | 'zeilen_weichen_ab';

export interface TorDiff {
    art: TorDiffArt;
    /** id der Belegzeile; fehlt bei den Groessen, die je Leistung gelten. */
    zeile?: string;
    serviceId?: string;
    entryId?: string;
    /** Wert des JSON-Wegs bzw. des Journal-Wegs (Menge, Tag oder Buchungsart). */
    json: number | string | null;
    journal: number | string | null;
    /** Ein Satz fuer einen Menschen. */
    text: string;
}

/** Was der Vergleich ueber den Beleg wissen muss — fuer beide Seiten derselbe. */
export interface TorRahmen {
    /** Der Belegzeitraum. Ohne Anfang und Ende entfaellt der Zeitraumdeckel. */
    zeitraum?: { from?: string; to?: string };
    /** Die Vorbelege (ohne den eigenen) — ihr Abgerechnetes mindert den Deckel. */
    vorbelege?: Array<Pick<Invoice, 'lines'>>;
    /**
     * Stufe 3a: das LV des Projekts, GESTEMPELT (stampFlatCoverage) wie im
     * Entwurfsaufbau — `vergleicheTorZuordnung` stempelt zur Sicherheit noch
     * einmal (idempotent). Ohne es entfallen Stichtag und Menge ohne Datum,
     * und die Buchungsart kennt keine Pauschaldeckung.
     */
    leistungsverzeichnis?: ServiceItem[];
    /** Stufe 3a: ein kumulativer Beleg — er baut seinen Entwurf ueber den Stand bis zum Stichtag. */
    kumulativ?: boolean;
    /**
     * Die am Beleg gespeicherte Wahl „ohne Datum mitnehmen"
     * (`details.includeUndated`). Nur sie entscheidet, ob ein Unterschied in
     * der Menge ohne Datum Geld bewegen kann; fehlt sie, wird konservativ
     * gemeldet (Kopf, Abschnitt „Stufe 3a", Punkt 2).
     */
    includeUndated?: boolean;
}

export interface TorVergleichEingang extends TorRahmen {
    /** Die Zeilen des JSON-Wegs — das Ergebnis, mit dem ausgestellt wird. */
    jsonZeilen: InvoiceLine[];
    /** Dieselben Zeilen, geplant aus dem Journal-Protokoll. */
    journalZeilen: InvoiceLine[];
    jsonLog: ServiceTransaction[];
    /** Das Journal als Protokoll (`journalToServiceTransactions`). */
    journalLog: ServiceTransaction[];
    /**
     * Die JSON-Seite, AM TOR vermessen (`vermesseSeite` mit DEMSELBEN Rahmen).
     * Fehlt sie, wird sie hier aus `jsonLog` vermessen — mit dem Register von
     * jetzt (Begruendung im Kopf, „Schnappschuss").
     */
    jsonSeite?: TorSeite;
}

/** Unterschiede ohne Geldwirkung je Groesse von Stufe 3a — gezaehlt, nicht bewertet. */
export interface TorOhneGeldwirkung {
    stichtag: number;
    undatiert: number;
    buchungsart: number;
}

export interface TorVergleich {
    /** true, wenn es keinen Befund gibt. Ein getauschter Gleichstand ist keiner, ein Unterschied ohne Geldwirkung auch nicht. */
    gleich: boolean;
    diffs: TorDiff[];
    /** Eintraege, die nur als Gleichstand getauscht wurden — gezaehlt, nicht bewertet. */
    getauscht: number;
    /** Stufe 3a: Unterschiede, die keine Menge bewegen (Geldprobe im Kopf). */
    ohneGeldwirkung: TorOhneGeldwirkung;
}

/** Rang je Art — fuer die stabile Ausgabereihenfolge innerhalb einer Zeile und in der Meldung. */
const RANG: Record<TorDiffArt, number> = {
    eintrag_fehlt_im_journal: 0,
    eintrag_nur_im_journal: 1,
    menge_weicht_ab: 2,
    tag_weicht_ab: 3,
    zeitraum_menge_weicht_ab: 4,
    stichtagsmenge_weicht_ab: 5,
    undatiert_weicht_ab: 6,
    buchungsart_weicht_ab: 7,
    zeilen_weichen_ab: 8,
};

// ═══════════════════════════════════════════════════════════════════════════
// Bausteine
// ═══════════════════════════════════════════════════════════════════════════

/** Woher die Buchungsart einer Zeile im Moment der Vermessung kam. */
type ArtQuelle = 'eintrag' | 'register' | 'rueckfall';

/** Die Buchungsart der ERSTEN Zeile je (Leistung, Vergleichskennung). */
interface ArtBefund {
    serviceId: string;
    entryId: string;
    /** Als Zahl gelesen; nur mit Menge > 0 ist eine Zeile ueberhaupt abrechenbar. */
    menge: number;
    /** Die bekannte Art — am Eintrag oder aus dem Register. Fehlt beim Rueckfall. */
    art?: ServiceJournalKind;
    quelle: ArtQuelle;
    /** Fuer den Text beim Rueckfall: der Beleg der Zeile. */
    beleg?: string;
}

/** Was der Entwurfsaufbau aus einem Protokoll macht, je Leistung (Stufe 3a). */
interface EntwurfsGroessen {
    /** Stand bis zum Stichtag, `computeCumulativePositions` ohne undatierte Mengen. Null ohne Stichtag. */
    stichtag: Map<string, number> | null;
    /** LV-Stand ohne Protokolleintrag (`undatedBacklog`). */
    luecke: Map<string, number>;
    /** Die Entwurfsmenge ohne bzw. mit „ohne Datum mitnehmen". Null, wo der Entwurf nichts deckelt. */
    ohne: Map<string, number> | null;
    mit: Map<string, number> | null;
}

/**
 * Was der Vergleich ueber EIN Protokoll wissen muss. Fuer den JSON-Weg wird
 * sie am Tor im synchronen Teil festgehalten (Kopf, „Schnappschuss").
 */
export interface TorSeite {
    /** Die ERSTE Protokollzeile je (Leistung, id) — dieselbe Regel wie im Nachtlauf. */
    zeilen: Map<string, ServiceTransaction>;
    /** Abrechenbare Menge je (Leistung, id), wie `allocatableEntries` sie sieht. */
    abrechenbar: Map<string, number>;
    /** (Leistung, Vergleichskennung) → rohe id; fuer den Hinweis auf eine uuid in anderer Schreibweise. */
    kennungen: Map<string, string>;
    /** Stufe 3a: abrechenbare Menge je (Leistung, Vergleichskennung) — fuer die Buchungsart. */
    abrechenbarVergleich: Map<string, number>;
    /** Stufe 3a: die Buchungsart je (Leistung, Vergleichskennung). */
    arten: Map<string, ArtBefund>;
    /** Zeitraumdeckel je Leistung (`zeitraumMengen`) — null ohne Zeitraum. */
    zeitraum: Map<string, number> | null;
    /** Stufe 3a — null ohne LV. */
    entwurf: EntwurfsGroessen | null;
    /** Je Leistung die Zeilen, die nie ins Journal koennen, aber links als Leistung zaehlen. */
    nichtAbbildbar: Map<string, { anzahl: number; menge: number }>;
}

const alsKarte = (positionen: Array<{ service: ServiceItem; openQty: number }>): Map<string, number> =>
    new Map(positionen.map((p) => [p.service.id, p.openQty]));

/**
 * Die Groessen des Entwurfsaufbaus aus einem Protokoll — `computeOpenPositions`
 * bzw. `computeCumulativePositions` selbst, mit den Argumenten, mit denen
 * buildProjectServiceLines sie ruft (ohne Reservierungen, Begruendung im Kopf).
 */
const entwurfsGroessen = (log: ServiceTransaction[], rahmen: TorRahmen): EntwurfsGroessen | null => {
    const lv = rahmen.leistungsverzeichnis;
    if (!lv || lv.length === 0) return null;
    const von = rahmen.zeitraum?.from || undefined;
    const bis = rahmen.zeitraum?.to || undefined;
    const luecke = new Map(undatedBacklog(lv, log).map((u) => [u.serviceId, u.quantity]));
    const stichtag = bis ? alsKarte(computeCumulativePositions(lv, { to: bis, serviceLog: log })) : null;
    if (rahmen.kumulativ) {
        // Kumulativ ohne Stichtag ist der Stand das LV selbst — ohne Protokoll.
        const mit = bis
            ? alsKarte(computeCumulativePositions(lv, { to: bis, serviceLog: log, includeUndated: true }))
            : null;
        return { stichtag, luecke, ohne: stichtag, mit };
    }
    // Fortschreibend ohne Zeitraum deckelt computeOpenPositions nichts.
    if (!von && !bis) return { stichtag, luecke, ohne: null, mit: null };
    const offen = (includeUndated: boolean): Map<string, number> =>
        alsKarte(
            computeOpenPositions(lv, new Map(), {
                from: von,
                to: bis,
                serviceLog: log,
                includeUndated,
                invoices: rahmen.vorbelege,
            }),
        );
    return { stichtag, luecke, ohne: offen(false), mit: offen(true) };
};

/**
 * Vermisst EIN Protokoll fuer den Vergleich. Liest ueber `isPostingEntry` und
 * `isBackfilledAltEntry` die Buchungsart — fuer JSON-Zeilen aus dem Register.
 * Fuer den JSON-Weg deshalb am Tor im synchronen Teil rufen.
 */
/**
 * Je Leistung die Protokollzeilen, die im JSON als Leistung zaehlen
 * (undatedQuantities: alles ausser Buchungsvorgaengen), im Journal aber nie
 * stehen koennen (`isJournalMappable`: ohne Datum oder ohne lesbare Menge).
 * Dieselbe Regel fuer den Vergleich am Tor und fuer den Rueckschatten des
 * Entwurfs (S5a Stufe 3c, entwurfAusJournal.ts) — beide nennen sie im Text,
 * keiner verschweigt sie.
 */
export const nichtAbbildbareZeilen = (
    log: ServiceTransaction[] | null | undefined,
): Map<string, { anzahl: number; menge: number }> => {
    const out = new Map<string, { anzahl: number; menge: number }>();
    for (const e of log ?? []) {
        if (!e?.serviceId || isAccountingEntry(e) || isJournalMappable(e)) continue;
        const n = out.get(e.serviceId) ?? { anzahl: 0, menge: 0 };
        n.anzahl += 1;
        n.menge += Number(e.quantity) || 0;
        out.set(e.serviceId, n);
    }
    return out;
};

export const vermesseSeite = (log: ServiceTransaction[] | null | undefined, rahmen: TorRahmen = {}): TorSeite => {
    const zeilen = new Map<string, ServiceTransaction>();
    const kennungen = new Map<string, string>();
    const arten = new Map<string, ArtBefund>();
    const nichtAbbildbar = nichtAbbildbareZeilen(log);
    for (const e of log ?? []) {
        if (!e?.serviceId) continue;
        if (!e.id) continue;
        const k = schluessel(e.serviceId, e.id);
        if (!zeilen.has(k)) zeilen.set(k, e);
        const v = schluessel(e.serviceId, kennungFuerVergleich(e.id));
        if (!kennungen.has(v)) kennungen.set(v, e.id);
        if (!arten.has(v)) {
            const art = buchungsartVon(e);
            const amEintrag = typeof e.kind === 'string' && e.kind.length > 0;
            arten.set(v, {
                serviceId: e.serviceId,
                entryId: e.id,
                menge: Number(e.quantity) || 0,
                art,
                quelle: art ? (amEintrag ? 'eintrag' : 'register') : 'rueckfall',
                beleg: typeof e.bookingReference === 'string' ? e.bookingReference : undefined,
            });
        }
    }
    const abrechenbar = new Map<string, number>();
    const abrechenbarVergleich = new Map<string, number>();
    for (const e of allocatableEntries(log ?? [])) {
        const k = schluessel(e.serviceId, e.id);
        abrechenbar.set(k, (abrechenbar.get(k) ?? 0) + e.quantity);
        const v = schluessel(e.serviceId, kennungFuerVergleich(e.id));
        abrechenbarVergleich.set(v, (abrechenbarVergleich.get(v) ?? 0) + e.quantity);
    }
    const von = rahmen.zeitraum?.from || undefined;
    const bis = rahmen.zeitraum?.to || undefined;
    return {
        zeilen,
        abrechenbar,
        kennungen,
        abrechenbarVergleich,
        arten,
        zeitraum: von || bis ? zeitraumMengen(log, { from: von, to: bis }, rahmen.vorbelege) : null,
        entwurf: entwurfsGroessen(log ?? [], rahmen),
        nichtAbbildbar,
    };
};

/**
 * Der Berliner Tag einer Zuordnung — aus dem Datum der PROTOKOLLZEILE dieser
 * Seite. Fehlt die Zeile, bleibt nur der eingefrorene Tag.
 */
const tagDerZuordnung = (seite: TorSeite, serviceId: string, a: InvoiceLineAllocation): string | null =>
    buchungstag(seite.zeilen.get(schluessel(serviceId, a.entryId))?.date) ?? buchungstag(a.date);

interface Anteil {
    menge: number;
    tag: string | null;
}

/** Zuordnung einer Zeile je Eintrag; eine doppelte id wird summiert. */
const anteileDerZeile = (zeile: InvoiceLine, seite: TorSeite): Map<string, Anteil> => {
    const out = new Map<string, Anteil>();
    const serviceId = zeile.serviceId ?? '';
    for (const a of zeile.allocations ?? []) {
        if (!a?.entryId) continue;
        const vorhanden = out.get(a.entryId);
        if (vorhanden) vorhanden.menge += a.quantity || 0;
        else out.set(a.entryId, { menge: a.quantity || 0, tag: tagDerZuordnung(seite, serviceId, a) });
    }
    return out;
};

/** Beide Seiten frieren zeichengleich dasselbe ein. */
const rohGleich = (x: InvoiceLineAllocation[] = [], y: InvoiceLineAllocation[] = []): boolean =>
    x.length === y.length &&
    x.every(
        (a, i) =>
            a?.entryId === y[i]?.entryId &&
            Math.abs((a?.quantity || 0) - (y[i]?.quantity || 0)) <= EPS &&
            (a?.date ?? '') === (y[i]?.date ?? ''),
    );

/** Der Zusatz fuer eine Leistung an der Tagesgrenze UTC/Berlin. */
const tagesgrenze = (e: ServiceTransaction | undefined): string => {
    const utc = asDay(e?.date);
    const berlin = buchungstag(e?.date);
    return utc && berlin && utc !== berlin
        ? ` (UTC-Tag ${utc}, Berliner Tag ${berlin} — Tagesgrenze, Entscheid 1)`
        : '';
};

/** Dieselbe uuid in anderer Schreibweise auf der anderen Seite? */
const andereSchreibweise = (seite: TorSeite, serviceId: string, entryId: string): string | null => {
    const roh = seite.kennungen.get(schluessel(serviceId, kennungFuerVergleich(entryId)));
    return roh && roh !== entryId ? roh : null;
};

/** Die Menge einer Leistung aus einer Karte, als Zahl. */
const wert = (karte: Map<string, number> | null | undefined, serviceId: string): number =>
    Number(karte?.get(serviceId) ?? 0);

// ═══════════════════════════════════════════════════════════════════════════
// Stufe 3a: Entwurfsgroessen und Buchungsart
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Eine Leistungszeile mit positiver ZEILENMENGE: Leistungszeile,
 * Leistungsbezug, keine reine Betragskorrektur, Menge > EPS. Die EINE Regel
 * fuer zwei Stellen — Stufe 3a misst nach ihr (`leistungenDesEntwurfs`), und
 * das Tor laesst nach ihr einen Beleg vorbei (`nurDurchgereicht` in
 * allocationGate.ts). Zwei Abschriften liefen frueher oder spaeter
 * auseinander, und dann liesse das Tor durch, was 3a prueft (Review 3b, P3).
 * Eine fehlende Menge gilt wie in planAllocations als 0; eine Menge, die
 * keine Zahl ist, zaehlt MIT — lieber prueft das Tor, als dass es
 * vorbeilaesst.
 */
export const istPositiveLeistungszeile = (l: InvoiceLine | null | undefined): boolean =>
    l?.kind === 'SERVICE' && !!l.serviceId && !l.quantityInformational && !(Number(l.quantity ?? 0) <= EPS);

/**
 * Die Leistungen, fuer die Stufe 3a misst: Leistungszeilen mit positiver,
 * abzurechnender Menge — genau die, die ein Entwurfsaufbau erzeugt und
 * `planAllocations` zuordnet. Storno, Gutschrift und Betragskorrektur nicht.
 */
const leistungenDesEntwurfs = (zeilen: InvoiceLine[]): string[] =>
    [...new Set(zeilen.filter(istPositiveLeistungszeile).map((l) => l.serviceId as string))].sort();

const vergleicheEntwurf = (
    leistungen: string[],
    json: TorSeite,
    journal: TorSeite,
    rahmen: TorRahmen,
    ohneGeldwirkung: TorOhneGeldwirkung,
): TorDiff[] => {
    const a = json.entwurf;
    const b = journal.entwurf;
    if (!a || !b) return [];
    const von = rahmen.zeitraum?.from || undefined;
    const bis = rahmen.zeitraum?.to || undefined;
    const entwurf = rahmen.kumulativ
        ? 'der kumulative Entwurf stuende auf'
        : `der Entwurf im Zeitraum ${von ?? '…'} bis ${bis ?? '…'} boete`;
    const diffs: TorDiff[] = [];
    for (const serviceId of leistungen) {
        // ── Stand bis zum Stichtag ─────────────────────────────────────
        if (a.stichtag && b.stichtag) {
            const x = wert(a.stichtag, serviceId);
            const y = wert(b.stichtag, serviceId);
            if (weichtAb(x, y)) {
                const ex = wert(a.ohne, serviceId);
                const ey = wert(b.ohne, serviceId);
                if (a.ohne && b.ohne && weichtAb(ex, ey)) {
                    diffs.push({
                        art: 'stichtagsmenge_weicht_ab',
                        serviceId,
                        json: x,
                        journal: y,
                        text: `Leistung ${serviceId}: bis zum Stichtag ${bis} erbracht ${zahl(x)} laut JSON gegen ${zahl(y)} laut Journal (ALT-Zeilen nur, soweit sie Leistung tragen) — ${entwurf} ${zahl(ex)} gegen ${zahl(ey)}${rahmen.kumulativ ? '' : ' an'}.`,
                    });
                } else {
                    ohneGeldwirkung.stichtag += 1;
                }
            }
        }
        // ── Menge ohne Datum ───────────────────────────────────────────
        // Ohne die Option floss die Luecke nicht in den Entwurf — dann bewegt
        // sie kein Geld, gleich was der Entwurf mit ihr anboete (Kopf,
        // Geldprobe). Unbekannt zaehlt wie mit: lieber eine Meldung zu viel.
        const ux = wert(a.luecke, serviceId);
        const uy = wert(b.luecke, serviceId);
        if (weichtAb(ux, uy)) {
            const mx = wert(a.mit, serviceId);
            const my = wert(b.mit, serviceId);
            if (rahmen.includeUndated !== false && a.mit && b.mit && weichtAb(mx, my)) {
                const n = json.nichtAbbildbar.get(serviceId);
                const zeilenOhne = n
                    ? `; ${n.anzahl} JSON-Zeile(n) dieser Leistung mit zusammen ${zahl(n.menge)} koennen nie ins Journal (ohne Datum oder lesbare Menge) — links zaehlen sie als protokolliert, rechts stehen sie in der Luecke`
                    : '';
                const wahl =
                    rahmen.includeUndated === true
                        ? 'Entwurf wurde mit „ohne Datum mitnehmen" gebaut'
                        : 'Wahl am Beleg nicht gespeichert — ob der Entwurf so gebaut wurde, ist offen';
                diffs.push({
                    art: 'undatiert_weicht_ab',
                    serviceId,
                    json: ux,
                    journal: uy,
                    text: `Leistung ${serviceId}: ohne Datum (LV-Stand ohne Protokolleintrag) ${zahl(ux)} laut JSON gegen ${zahl(uy)} laut Journal — mit „ohne Datum mitnehmen" ${entwurf} ${zahl(mx)} gegen ${zahl(my)}${rahmen.kumulativ ? '' : ' an'} (${wahl})${zeilenOhne}.`,
                });
            } else {
                ohneGeldwirkung.undatiert += 1;
            }
        }
    }
    return diffs;
};

const artText = (a: ArtBefund): string => {
    if (a.quelle === 'register') return `Art ${a.art} aus dem Register`;
    if (a.quelle === 'eintrag') return `Art ${a.art} am Eintrag`;
    return `ohne bekannte Art, nach Beleg${a.beleg ? ` „${a.beleg.slice(0, 40)}"` : ''} und Text`;
};

const vergleicheBuchungsart = (
    leistungen: string[],
    json: TorSeite,
    journal: TorSeite,
    vorbelege: Array<Pick<Invoice, 'lines'>> | undefined,
    /** Positionen, die eine Monatspauschale deckt (`flatCoveredBy` aus dem gestempelten LV). */
    gedeckt: ReadonlySet<string>,
    ohneGeldwirkung: TorOhneGeldwirkung,
): TorDiff[] => {
    const relevant = new Set(leistungen);
    if (relevant.size === 0) return [];
    const vergeben = allocatedByEntry(vorbelege ?? []);
    const diffs: TorDiff[] = [];
    for (const [k, links] of json.arten) {
        if (!relevant.has(links.serviceId)) continue;
        const rechts = journal.arten.get(k);
        // Fehlt der Eintrag auf einer Seite, ist das keine Frage der Art — das
        // meldet die Zuordnung, sobald er gezogen wird. Ohne Menge > 0 ist er
        // auf keiner Seite abrechenbar.
        if (!rechts || !(links.menge > EPS) || !(rechts.menge > EPS)) continue;
        const abrLinks = json.abrechenbarVergleich.has(k);
        const abrRechts = journal.abrechenbarVergleich.has(k);
        if (abrLinks === abrRechts) continue;
        // Eine gedeckte Position steht mit ihrem Geldstand still: kumulativ
        // ist ihr Zuwachs 0 (Menge = quantityBilled), fortschreibend ihre
        // offene Menge 0 (openQuantity). Ihre Eintraege ordnet auf keinem Weg
        // jemand zu — ob einer davon Buchung oder Arbeit ist, aendert den
        // Betrag nicht (Codex-Review S5a 3a, P2).
        if (gedeckt.has(links.serviceId)) {
            ohneGeldwirkung.buchungsart += 1;
            continue;
        }
        const menge = (abrLinks ? json.abrechenbarVergleich.get(k) : journal.abrechenbarVergleich.get(k)) ?? 0;
        const offen = menge - (vergeben.get(kennungFuerVergleich(links.entryId)) ?? 0);
        if (!(offen > EPS)) {
            ohneGeldwirkung.buchungsart += 1;
            continue;
        }
        diffs.push({
            art: 'buchungsart_weicht_ab',
            serviceId: links.serviceId,
            entryId: links.entryId,
            json: abrLinks ? 'abrechenbar' : 'Buchung',
            journal: abrRechts ? 'abrechenbar' : 'Buchung',
            text: `Leistung ${links.serviceId}: Eintrag ${links.entryId} (Menge ${zahl(menge)}, davon offen ${zahl(offen)}) ist am Tor laut JSON-Weg ${abrLinks ? 'abrechenbar' : 'eine Buchung'} (${artText(links)}), laut Journal ${abrRechts ? 'abrechenbar' : 'eine Buchung'} (kind ${rechts.art ?? '—'}) — nach dem Umschalten ${abrRechts ? 'wuerde er abgerechnet' : 'fiele er aus der Abrechnung'}.`,
        });
    }
    return diffs.sort(
        (p, q) => (p.serviceId ?? '').localeCompare(q.serviceId ?? '') || (p.entryId ?? '').localeCompare(q.entryId ?? ''),
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// Der Vergleich
// ═══════════════════════════════════════════════════════════════════════════

export const vergleicheTorZuordnung = (eingang: TorVergleichEingang): TorVergleich => {
    const jsonZeilen = eingang.jsonZeilen ?? [];
    const journalZeilen = eingang.journalZeilen ?? [];
    // Gestempelt wird hier noch einmal: der Stempel ist idempotent, und ein
    // Aufrufer mit ungestempeltem LV saehe sonst eine gedeckte Position als
    // offen — in der Entwurfsmenge wie in der Buchungsart.
    const rahmen: TorRahmen = {
        zeitraum: eingang.zeitraum,
        vorbelege: eingang.vorbelege,
        leistungsverzeichnis: eingang.leistungsverzeichnis?.length
            ? stampFlatCoverage(eingang.leistungsverzeichnis)
            : eingang.leistungsverzeichnis,
        kumulativ: eingang.kumulativ,
        includeUndated: eingang.includeUndated,
    };
    const json = eingang.jsonSeite ?? vermesseSeite(eingang.jsonLog, rahmen);
    const journal = vermesseSeite(eingang.journalLog, rahmen);

    const diffs: TorDiff[] = [];
    let getauscht = 0;
    const ohneGeldwirkung: TorOhneGeldwirkung = { stichtag: 0, undatiert: 0, buchungsart: 0 };

    /** Kann dieser Eintrag Teil eines getauschten Gleichstands sein? */
    const gleichstandFaehig = (serviceId: string, entryId: string): boolean => {
        const k = schluessel(serviceId, entryId);
        const mengeJson = json.abrechenbar.get(k);
        const mengeJournal = journal.abrechenbar.get(k);
        if (mengeJson === undefined || mengeJournal === undefined) return false;
        if (Math.abs(mengeJson - mengeJournal) > EPS) return false;
        const tagJson = buchungstag(json.zeilen.get(k)?.date);
        return !!tagJson && tagJson === buchungstag(journal.zeilen.get(k)?.date);
    };

    const warumNurJson = (serviceId: string, entryId: string): string => {
        const k = schluessel(serviceId, entryId);
        const zeile = json.zeilen.get(k);
        if (!journal.zeilen.has(k)) {
            if (zeile && !isJournalMappable(zeile)) {
                return 'kann nie im Journal stehen (nicht abbildbar, z. B. ohne Datum) — der Journal-Weg forderte ihn nie';
            }
            const anders = andereSchreibweise(journal, serviceId, entryId);
            if (anders) {
                return `steht im Journal als ${anders} — nur die Schreibweise der uuid weicht ab; die Vorbelege treffen ihn dort nicht`;
            }
            return 'steht nicht im Journal — der Journal-Weg forderte ihn nie';
        }
        if (!journal.abrechenbar.has(k)) return 'steht im Journal, ist dort aber nicht abrechenbar (Buchungsart oder Menge)';
        return `steht auf beiden Seiten, der Journal-Weg zog ihn aber nicht${tagesgrenze(zeile)}`;
    };

    const warumNurJournal = (serviceId: string, entryId: string): string => {
        const k = schluessel(serviceId, entryId);
        if (!json.zeilen.has(k)) {
            const anders = andereSchreibweise(json, serviceId, entryId);
            if (anders) {
                return `steht im JSON als ${anders} — nur die Schreibweise der uuid weicht ab; die Vorbelege treffen ihn im Journal nicht`;
            }
            return 'steht nur im Journal — der Journal-Weg forderte ihn zusaetzlich';
        }
        if (!json.abrechenbar.has(k)) return 'steht im JSON, ist dort aber nicht abrechenbar (Buchungsart oder Menge)';
        return `steht auf beiden Seiten, der JSON-Weg zog ihn aber nicht${tagesgrenze(json.zeilen.get(k))}`;
    };

    // ── Je Belegzeile ─────────────────────────────────────────────────────
    const zeilen = Math.max(jsonZeilen.length, journalZeilen.length);
    for (let i = 0; i < zeilen; i += 1) {
        const links = jsonZeilen[i];
        const rechts = journalZeilen[i];
        // Beide Seiten entstehen aus DENSELBEN Zeilen (planAllocations bildet
        // sie 1:1 ab). Passt das nicht, ist jeder weitere Vergleich sinnlos.
        if (!links || !rechts || links.id !== rechts.id) {
            diffs.push({
                art: 'zeilen_weichen_ab',
                zeile: links?.id ?? rechts?.id,
                json: links?.id ?? null,
                journal: rechts?.id ?? null,
                text: `Belegzeile ${i + 1}: JSON-Weg ${links?.id ?? '—'}, Journal-Weg ${rechts?.id ?? '—'} — die Zeilen passen nicht zueinander.`,
            });
            continue;
        }
        if (rohGleich(links.allocations, rechts.allocations)) continue;

        const serviceId = links.serviceId ?? '';
        const a = anteileDerZeile(links, json);
        const b = anteileDerZeile(rechts, journal);
        const dieseZeile: TorDiff[] = [];
        const kandidaten: Array<{ entryId: string; x?: Anteil; y?: Anteil }> = [];

        for (const entryId of [...new Set([...a.keys(), ...b.keys()])].sort()) {
            const x = a.get(entryId);
            const y = b.get(entryId);
            if (x && y && Math.abs(x.menge - y.menge) <= EPS) {
                if (x.tag !== y.tag) {
                    dieseZeile.push({
                        art: 'tag_weicht_ab',
                        zeile: links.id,
                        serviceId,
                        entryId,
                        json: x.tag,
                        journal: y.tag,
                        text: `Zeile ${links.id}: Eintrag ${entryId} (Leistung ${serviceId}) liegt im JSON am Berliner Tag ${x.tag ?? '—'}, im Journal am ${y.tag ?? '—'}.`,
                    });
                }
                continue;
            }
            kandidaten.push({ entryId, x, y });
        }

        // Gleichstaende je Berliner Tag aussortieren (Begruendung im Kopf).
        const jeTag = new Map<string, typeof kandidaten>();
        for (const k of kandidaten) {
            const tag = k.x?.tag ?? k.y?.tag ?? '';
            const liste = jeTag.get(tag);
            if (liste) liste.push(k);
            else jeTag.set(tag, [k]);
        }
        for (const gruppe of jeTag.values()) {
            const saldo = gruppe.reduce((s, k) => s + (k.x?.menge ?? 0) - (k.y?.menge ?? 0), 0);
            if (Math.abs(saldo) <= EPS && gruppe.every((k) => gleichstandFaehig(serviceId, k.entryId))) {
                getauscht += gruppe.length;
                continue;
            }
            for (const { entryId, x, y } of gruppe) {
                if (x && y) {
                    const k = schluessel(serviceId, entryId);
                    const ej = json.abrechenbar.get(k);
                    const en = journal.abrechenbar.get(k);
                    const erbracht =
                        ej !== undefined && en !== undefined && Math.abs(ej - en) > EPS
                            ? ` — erbracht ${zahl(ej)} im JSON gegen ${zahl(en)} im Journal`
                            : '';
                    dieseZeile.push({
                        art: 'menge_weicht_ab',
                        zeile: links.id,
                        serviceId,
                        entryId,
                        json: x.menge,
                        journal: y.menge,
                        text: `Zeile ${links.id}: Eintrag ${entryId} (Leistung ${serviceId}) mit ${zahl(x.menge)} im JSON gegen ${zahl(y.menge)} im Journal zugeordnet${erbracht}.`,
                    });
                } else if (x) {
                    dieseZeile.push({
                        art: 'eintrag_fehlt_im_journal',
                        zeile: links.id,
                        serviceId,
                        entryId,
                        json: x.menge,
                        journal: 0,
                        text: `Zeile ${links.id}: Eintrag ${entryId} (Leistung ${serviceId}, ${zahl(x.menge)}, Tag ${x.tag ?? 'ohne'}) nur im JSON zugeordnet — ${warumNurJson(serviceId, entryId)}.`,
                    });
                } else if (y) {
                    dieseZeile.push({
                        art: 'eintrag_nur_im_journal',
                        zeile: links.id,
                        serviceId,
                        entryId,
                        json: 0,
                        journal: y.menge,
                        text: `Zeile ${links.id}: Eintrag ${entryId} (Leistung ${serviceId}, ${zahl(y.menge)}, Tag ${y.tag ?? 'ohne'}) nur im Journal zugeordnet — ${warumNurJournal(serviceId, entryId)}.`,
                    });
                }
            }
        }

        dieseZeile.sort((p, q) => RANG[p.art] - RANG[q.art] || (p.entryId ?? '').localeCompare(q.entryId ?? ''));
        diffs.push(...dieseZeile);
    }

    // ── Zeitraumdeckel je Leistung ────────────────────────────────────────
    // Ohne Anfang und Ende deckelt `computeOpenPositions` nichts — dann gibt
    // es auch nichts zu vergleichen.
    const von = eingang.zeitraum?.from || undefined;
    const bis = eingang.zeitraum?.to || undefined;
    if (von || bis) {
        const leistungen = [
            ...new Set(
                jsonZeilen
                    .filter((l) => l?.kind === 'SERVICE' && !!l.serviceId)
                    .map((l) => l.serviceId as string),
            ),
        ].sort();
        if (leistungen.length > 0) {
            for (const serviceId of leistungen) {
                // `Number`: eine krumme Menge im JSON (Text) addiert
                // `loggedQuantityInPeriod` als Text mit — der Entwurf rechnet
                // damit genauso. Gemeldet wird sie als NaN, statt hier an
                // `toFixed` zu scheitern.
                const x = wert(json.zeitraum, serviceId);
                const y = wert(journal.zeitraum, serviceId);
                if (Math.abs(x - y) <= EPS) continue;
                diffs.push({
                    art: 'zeitraum_menge_weicht_ab',
                    serviceId,
                    json: x,
                    journal: y,
                    text: `Leistung ${serviceId}: im Zeitraum ${von ?? '…'} bis ${bis ?? '…'} offen ${zahl(x)} laut JSON gegen ${zahl(y)} laut Journal (beidseitig Berliner Tag).`,
                });
            }
        }
    }

    // ── Stufe 3a: Stichtag, Menge ohne Datum, Buchungsart ─────────────────
    const leistungen3a = leistungenDesEntwurfs(jsonZeilen);
    diffs.push(...vergleicheEntwurf(leistungen3a, json, journal, rahmen, ohneGeldwirkung));
    const gedeckt = new Set(
        (rahmen.leistungsverzeichnis ?? []).filter((s) => !!s?.flatCoveredBy).map((s) => s.id),
    );
    diffs.push(...vergleicheBuchungsart(leistungen3a, json, journal, eingang.vorbelege, gedeckt, ohneGeldwirkung));

    return { gleich: diffs.length === 0, diffs, getauscht, ohneGeldwirkung };
};

// ═══════════════════════════════════════════════════════════════════════════
// Die Meldung
// ═══════════════════════════════════════════════════════════════════════════

/** Was nach `bug_reports` geht — die Form von `meldeImHintergrund` (bugReportService). */
export interface TorMeldung {
    message: string;
    stack: string;
}

/** Hoechstens so viele Abweichungen ausgeschrieben; der Rest wird gezaehlt. */
const MAX_ZEILEN = 20;
/** `report_bug` kappt den Stack bei 8000 Zeichen — lieber selbst und sichtbar. */
const STACK_GRENZE = 7_900;

/**
 * Beleg, Projekt und Zeitraum einer Meldung — fuer den Schatten (JSON-Modus)
 * und das Tor (Stufe 3b, allocationGate.ts) dieselbe Form, damit die
 * Tor-Ueberwachung beide mit denselben Mustern liest.
 */
export interface TorKopf {
    projectId: string;
    projectNumber: string | null;
    invoiceId: string;
    invoiceNumber: string | null;
    zeitraum: { from?: string; to?: string };
}

/**
 * Der Kopf aus dem Eingang. Wirft nie — er steht auch in der Meldung, die
 * einen kaputten Eingang meldet.
 */
export const torKopf = (eingang: SchattenAmTorEingang | null | undefined): TorKopf => ({
    projectId: String(eingang?.projectId ?? '?'),
    projectNumber: eingang?.projectNumber ? String(eingang.projectNumber) : null,
    invoiceId: String(eingang?.invoice?.id ?? '?'),
    invoiceNumber: eingang?.invoice?.invoiceNumber ? String(eingang.invoice.invoiceNumber) : null,
    zeitraum: { from: eingang?.zeitraum?.from, to: eingang?.zeitraum?.to },
});

/**
 * Beleg UND Projekt stehen in der MELDUNG und nicht nur im Stack: `report_bug`
 * fasst nach Fingerabdruck (Meldung + erste Stackzeile) zusammen und zaehlt
 * dann nur noch hoch — der Kontext der zweiten Meldung ginge verloren. So ist
 * jede Ausstellung je Projekt eine eigene Zeile; ein Wiederholungsversuch
 * desselben Belegs zaehlt dieselbe Zeile hoch, und das ist richtig.
 */
export const torMeldungsTitel = (was: string, kopf: TorKopf): string => {
    const beleg = kopf.invoiceNumber ? `${kopf.invoiceNumber} (${kopf.invoiceId})` : kopf.invoiceId;
    const projekt = kopf.projectNumber ? `${kopf.projectNumber} (${kopf.projectId})` : kopf.projectId;
    return `[S5a-Tor] ${was} — Beleg ${beleg}, Projekt ${projekt}`;
};

/**
 * Warum der Schatten laeuft. Seit Stufe 3b sperrt im Normalbetrieb das Tor
 * selbst (allocationGate.ts); der Schatten laeuft nur noch in zwei Faellen:
 *   - 'notschalter': ein Administrator hat den Tor-Notschalter umgelegt
 *     (JSON-Modus, fuer alle Belege);
 *   - 'durchgereicht': der Beleg traegt in diesem Projekt keine
 *     Leistungszeile mit positiver Menge — Storno, kaufmaennische Gutschrift,
 *     reine Betragskorrektur (`nurDurchgereicht`, Controller-Entscheid
 *     23.10.2026). Dort schuetzt das Tor kein Geld, eine Sperre verhinderte
 *     nur die Korrektur.
 */
export type SchattenAnlass = 'notschalter' | 'durchgereicht';

/**
 * Die erste Stackzeile jeder Meldung. Sie nennt Stufe und Anlass — die
 * Tor-Ueberwachung erkennt daran Meldungen eines alten Programmstands und
 * trennt Notschalter-Meldungen von denen durchgereichter Belege.
 */
const ohneWirkung = (anlass: SchattenAnlass): string =>
    anlass === 'durchgereicht'
        ? 'S5a Stufe 3b, Tor ohne Pruefung (keine Leistungszeile mit positiver Menge: Storno, Gutschrift oder reine Betragskorrektur), Schatten am Tor — OHNE Wirkung: ausgestellt wurde mit der Zuordnung aus dem JSON.'
        : 'S5a Stufe 3b, Notschalter AN (JSON-Modus), Schatten am Tor — OHNE Wirkung: ausgestellt wurde mit der Zuordnung aus dem JSON.';

export const kappeStack = (s: string): string =>
    s.length > STACK_GRENZE ? `${s.slice(0, STACK_GRENZE)}\n…(gekuerzt)` : s;

/** Die Arten einer Abweichung in fester Reihenfolge: „2x eintrag_fehlt_im_journal, 1x …". */
const artenZeile = (diffs: TorDiff[]): string => {
    const jeArt = new Map<TorDiffArt, number>();
    for (const d of diffs) jeArt.set(d.art, (jeArt.get(d.art) ?? 0) + 1);
    return (Object.keys(RANG) as TorDiffArt[])
        .filter((art) => jeArt.has(art))
        .map((art) => `${jeArt.get(art)}x ${art}`)
        .join(', ');
};

/**
 * Wie eine Abweichungsmeldung aussieht. Ohne Angabe die des Schattens; das
 * Tor (Stufe 3b) schreibt mit DIESER Funktion seine Sperr- und Hinweis-
 * meldungen — dieselbe Zeile „Arten: …", dieselben Zaehler, damit die
 * Ueberwachung sie mit denselben Mustern liest.
 */
export interface AbweichungsForm {
    /** Was im Titel steht (`torMeldungsTitel`). */
    was: string;
    /** Die erste Stackzeile — sie nennt Stufe und Wirkung. */
    ersteZeile: string;
    /** Zeilen nach „Arten: …" (z. B. „Sperrend: 2 von 3"). */
    zusatz?: string[];
    /** Die Zeile je Abweichung; ohne Angabe „- [art] text". */
    zeile?: (d: TorDiff) => string;
    /** Die Abweichungen in der Reihenfolge der Ausgabe; ohne Angabe die des Vergleichs. */
    reihenfolge?: TorDiff[];
    /** Zusaetzliche Felder fuer die JSON-Zeile am Ende. */
    json?: Record<string, unknown>;
}

/** Die Form der Schattenmeldung je Anlass. */
export const schattenForm = (anlass: SchattenAnlass = 'notschalter'): AbweichungsForm => ({
    was: 'Zuordnung aus dem Journal weicht ab',
    ersteZeile: ohneWirkung(anlass),
});

export const abweichungsMeldung = (
    kopf: TorKopf,
    ergebnis: TorVergleich,
    form: AbweichungsForm = schattenForm(),
): TorMeldung => {
    const o = ergebnis.ohneGeldwirkung ?? { stichtag: 0, undatiert: 0, buchungsart: 0 };
    const liste = form.reihenfolge ?? ergebnis.diffs;
    const zeile = form.zeile ?? ((d: TorDiff) => `- [${d.art}] ${d.text}`);
    const zeilen = [
        form.ersteZeile,
        `Zeitraum: ${kopf.zeitraum.from ?? '…'} bis ${kopf.zeitraum.to ?? '…'}`,
        `Arten: ${artenZeile(ergebnis.diffs)} (${ergebnis.diffs.length} Abweichungen; ${ergebnis.getauscht} Eintraege als Gleichstand getauscht, nicht bewertet; ohne Geldwirkung: ${o.stichtag} Stichtag, ${o.undatiert} ohne Datum, ${o.buchungsart} Buchungsart)`,
        ...(form.zusatz ?? []),
        ...liste.slice(0, MAX_ZEILEN).map(zeile),
        ...(liste.length > MAX_ZEILEN ? [`… und ${liste.length - MAX_ZEILEN} weitere`] : []),
        JSON.stringify({
            ...kopf,
            anzahl: ergebnis.diffs.length,
            getauscht: ergebnis.getauscht,
            ohneGeldwirkung: o,
            ...(form.json ?? {}),
        }),
    ];
    return { message: torMeldungsTitel(form.was, kopf), stack: kappeStack(zeilen.join('\n')) };
};

/**
 * Wie viele Eintraege eines Protokolls im Journal stehen MUESSTEN: abbildbar
 * (`isJournalMappable`) und keine Buchungsvorgaenge (BU-/VZ-/RN-/MB-). Kommt
 * das Journal leer zurueck, obwohl es welche gibt, ist es fuer diesen Nutzer
 * nicht lesbar (RLS) — dieselbe Regel fuer das Tor und den Schatten.
 */
export const abbildbareLeistungen = (log: ServiceTransaction[] | null | undefined): number =>
    (log ?? []).filter((e) => isJournalMappable(e) && !isAccountingEntry(e)).length;

/**
 * `leerTrotz` > 0: das Journal kam leer zurueck, das Protokoll fuehrt aber so
 * viele abbildbare Leistungen (vermutlich Leserecht) — Titel und erste Zeile
 * bleiben die des Lesefehlers.
 */
export const nichtLesbarMeldung = (kopf: TorKopf, anlass: SchattenAnlass = 'notschalter', leerTrotz = 0): TorMeldung => ({
    message: torMeldungsTitel('Journal nicht lesbar am Tor', kopf),
    stack: kappeStack(
        [
            ohneWirkung(anlass),
            leerTrotz > 0
                ? `loadJournal lieferte KEINE Zeile, das Projektprotokoll fuehrt aber ${leerTrotz} abbildbare Leistungen — vermutlich fehlt das Leserecht (service_journal: is_admin() or can_access_project), verglichen wurde nichts.`
                : 'loadJournal lieferte null (Tabelle fehlt, kein Netz oder keine Berechtigung) — verglichen wurde nichts.',
            anlass === 'durchgereicht'
                ? 'Keine Leistungszeile mit positiver Menge: hier sperrt das Tor nie, auch nicht bei einem Lesefehler — es wird nur gemeldet.'
                : 'Notschalter AN: im JSON-Modus wird nur gemeldet. Im Journal-Modus sperrt hier das Tor (Entscheid 3).',
            JSON.stringify({
                ...kopf,
                ...(leerTrotz > 0 ? { leerTrotz } : {}),
                online: typeof navigator !== 'undefined' ? navigator.onLine : null,
            }),
        ].join('\n'),
    ),
});

export const ausfallMeldung = (kopf: TorKopf, fehler: unknown, anlass: SchattenAnlass = 'notschalter'): TorMeldung => {
    const e = (fehler ?? {}) as { message?: string; stack?: string };
    return {
        message: torMeldungsTitel('Schatten am Tor ausgefallen', kopf),
        stack: kappeStack(
            [
                ohneWirkung(anlass),
                `Fehler: ${String(e.message ?? fehler).slice(0, 300)}`,
                e.stack ?? '',
                JSON.stringify(kopf),
            ].join('\n'),
        ),
    };
};

/**
 * Der Hintergrundkanal (`meldeImHintergrund`, wie `meldeSpiegelFehler` in
 * serviceJournal.ts): dieselbe RPC wie `captureBug`, aber ohne dessen Sperre
 * — eine Abweichung geht nicht verloren, weil gerade ein Nutzerfehler
 * gemeldet wird, und verdraengt ihn auch nicht — und ohne Gemini. Er drosselt
 * selbst (5 min je Fingerabdruck, 20 je Sitzung, getrennt von `captureBug`)
 * und wirft nie. Nur im Browser — im Test und im Nachtlauf gibt es kein
 * `window`, und dort soll auch nichts in `bug_reports` landen. Das Tor
 * (Stufe 3b) meldet ueber genau diesen Kanal.
 */
export const meldeUeberHintergrundkanal = async (m: TorMeldung): Promise<void> => {
    if (typeof window === 'undefined') return;
    try {
        const { meldeImHintergrund } = await import('./bugReportService');
        await meldeImHintergrund({ message: m.message, stack: m.stack, source: 'window' });
    } catch {
        // Die Meldung ist eine Zugabe: sie darf die Ausstellung nie beruehren.
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// Die Huelle — wirft nie, gibt nichts zurueck
// ═══════════════════════════════════════════════════════════════════════════

export interface SchattenAmTorEingang {
    projectId: string;
    projectNumber?: string | null;
    /**
     * `details.billingMode` entscheidet in Stufe 3a, welcher Entwurfsaufbau
     * verglichen wird; `details.includeUndated`, ob die Menge ohne Datum
     * darin Geld bewegen kann.
     */
    invoice: Pick<Invoice, 'id'> & Partial<Pick<Invoice, 'invoiceNumber' | 'details'>>;
    /** Dieselben Zeilen, die in planAllocations gingen. */
    projectLines: InvoiceLine[];
    /** Das Protokoll aus dem JSON, aus dem zugeordnet wurde. */
    jsonLog: ServiceTransaction[];
    /** Die Vorbelege ohne den eigenen — dieselben wie im JSON-Weg. */
    prior: Array<Pick<Invoice, 'lines'>>;
    /** Der Belegzeitraum, wie er in planAllocations ging. */
    zeitraum: { from?: string; to?: string };
    /** Die Zuwachsfunktion (kumulativ), wie sie in planAllocations ging. */
    quantityOf?: (line: InvoiceLine) => number;
    /** Das Ergebnis des JSON-Wegs — damit wird ausgestellt. */
    jsonLines: InvoiceLine[];
    /**
     * Stufe 3a: das LV, das planProjectIssue geladen hat (`services`). Ohne
     * es entfallen Stichtag und Menge ohne Datum; der Rest bleibt.
     */
    services?: ServiceItem[];
}

export interface SchattenAmTorDeps {
    ladeJournal: (projectId: string) => Promise<ServiceJournalRow[] | null>;
    plane: typeof planAllocations;
    melde: (m: TorMeldung) => void | Promise<void>;
}

export const STANDARD_TOR_DEPS: SchattenAmTorDeps = {
    ladeJournal: loadJournal,
    plane: planAllocations,
    melde: meldeUeberHintergrundkanal,
};

/** Eine Zeile samt Zuordnung kopieren — der Rest der Ausstellung laeuft weiter. */
const zeilenKopie = (lines: InvoiceLine[] | null | undefined): InvoiceLine[] =>
    (lines ?? []).map((l) =>
        l && Array.isArray(l.allocations) ? { ...l, allocations: l.allocations.map((a) => ({ ...a })) } : l && { ...l },
    );

/** Meldet und schluckt jeden Fehler der Meldung selbst. */
export const sicherMelden = async (deps: Pick<SchattenAmTorDeps, 'melde'>, m: TorMeldung): Promise<void> => {
    try {
        await deps.melde(m);
    } catch {
        // Auch eine kaputte Meldung darf nicht nach aussen dringen.
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// Schnappschuss und Journal-Weg — gemeinsam fuer Schatten und Tor
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Was beim Ausstellen SYNCHRON festgehalten wird, bevor das Journal gelesen
 * wird — fuer den Schatten (JSON-Modus) wie fuer das Tor (Stufe 3b).
 *
 * Bis das Journal da ist, kann sich zweierlei aendern: der Schatten laeuft
 * ohne `await`, die Ausstellung arbeitet an ihren Objekten weiter; und das
 * Buchungsart-Register ist global — ein anderer Tab kann bis dahin ein
 * Projekt oeffnen oder schliessen. Beides darf den Vergleich nicht
 * verfaelschen (Kopf, „Schnappschuss"). Das Tor wartet zwar auf das Journal,
 * das Register aendert sich aber auch dann.
 */
export interface TorSchnappschuss {
    kopf: TorKopf;
    /** Kopien der Zeilen, die in planAllocations gingen. */
    projectLines: InvoiceLine[];
    /** Kopien des Ergebnisses des JSON-Wegs. */
    jsonZeilen: InvoiceLine[];
    jsonLog: ServiceTransaction[];
    /** Die vergebene Menge je Eintrag aus den Vorbelegen — wie im JSON-Weg. */
    vergeben: Map<string, number>;
    zeitraum: { from?: string; to?: string };
    quantityOf?: (line: InvoiceLine) => number;
    rahmen: TorRahmen;
    /** Die JSON-Seite, in diesem Moment vermessen. */
    jsonSeite: TorSeite;
}

/**
 * Haelt den Schnappschuss fest (Zeilen und LV kopiert, vergebene Mengen aus
 * den Vorbelegen gerechnet, Belegart und die Wahl „ohne Datum mitnehmen"
 * gelesen, die JSON-Seite vermessen). Das Protokoll selbst wird nur als Liste
 * kopiert — seine Zeilen veraendert niemand. Das kostet Millisekunden, keine
 * Rundreise.
 */
export const schnappschussAmTor = (eingang: SchattenAmTorEingang, kopf: TorKopf = torKopf(eingang)): TorSchnappschuss => {
    const projectLines = zeilenKopie(eingang.projectLines);
    const jsonZeilen = zeilenKopie(eingang.jsonLines);
    const jsonLog = (eingang.jsonLog ?? []).slice();
    // Die Vorbelege sind frisch geladen und gehoeren allein
    // planProjectIssue — niemand sonst haelt sie. Kopiert wird nichts,
    // gerechnet wird die vergebene Menge sofort, wie im JSON-Weg.
    const prior = eingang.prior ?? [];
    const vergeben = allocatedByEntry(prior);
    const zeitraum = { from: kopf.zeitraum.from, to: kopf.zeitraum.to };
    // Stufe 3a: das LV als Kopie (die Rueckschreibung rechnet danach mit
    // denselben Objekten weiter) und gestempelt wie in
    // getNormalizedServices — eine Pauschalposition steht im Entwurf
    // still, ihr Stand haengt dann an keinem Protokoll.
    const leistungsverzeichnis =
        Array.isArray(eingang.services) && eingang.services.length > 0
            ? stampFlatCoverage(eingang.services.map((s) => ({ ...s })))
            : undefined;
    // Die Wahl „ohne Datum mitnehmen" am Beleg — jetzt gelesen, wie alles
    // andere am Beleg. Nur ein echter Wahrheitswert zaehlt: was sonst im
    // jsonb steht (Text, null), ist so unbekannt wie ein fehlendes Feld,
    // und unbekannt wird gemeldet statt verschwiegen.
    const wahl: unknown = eingang.invoice?.details?.includeUndated;
    const rahmen: TorRahmen = {
        zeitraum,
        vorbelege: prior,
        leistungsverzeichnis,
        kumulativ: eingang.invoice?.details?.billingMode === 'CUMULATIVE',
        includeUndated: typeof wahl === 'boolean' ? wahl : undefined,
    };
    // Die JSON-Seite JETZT vermessen: hier sieht sie das Register so, wie
    // planAllocations es eben sah (Kopf, „Schnappschuss").
    const jsonSeite = vermesseSeite(jsonLog, rahmen);
    return {
        kopf,
        projectLines,
        jsonZeilen,
        jsonLog,
        vergeben,
        zeitraum,
        quantityOf: eingang.quantityOf,
        rahmen,
        jsonSeite,
    };
};

/** Der Journal-Weg und sein Vergleich mit dem JSON-Weg. */
export interface TorJournalWeg {
    /** Dieselben Zeilen, zugeordnet aus dem Journal — mit DIESEN stellt das Tor aus. */
    journalZeilen: InvoiceLine[];
    journalLog: ServiceTransaction[];
    ergebnis: TorVergleich;
}

/**
 * Rechnet die Zuordnung aus dem Journal — `journalToServiceTransactions` und
 * danach `planAllocations` mit GENAU den uebrigen Eingaben des JSON-Wegs — und
 * vergleicht beide mit `vergleicheTorZuordnung`. Rein bis auf `plane`.
 */
export const journalwegAmTor = (
    s: TorSchnappschuss,
    journal: ServiceJournalRow[],
    plane: typeof planAllocations = planAllocations,
): TorJournalWeg => {
    const journalLog = journalToServiceTransactions(journal);
    // Ohne die verzichteten Eintraege — dieselbe Filterung wie der JSON-Weg in
    // planProjectIssue, ueber dasselbe LV (allocationLogic.ohneVerzichteteEintraege).
    const journalZeilen = plane(
        s.projectLines,
        ohneVerzichteteEintraege(journalLog, s.rahmen.leistungsverzeichnis),
        s.vergeben,
        s.zeitraum,
        s.quantityOf,
    );
    const ergebnis = vergleicheTorZuordnung({
        ...s.rahmen,
        jsonZeilen: s.jsonZeilen,
        journalZeilen,
        jsonLog: s.jsonLog,
        journalLog,
        jsonSeite: s.jsonSeite,
    });
    return { journalZeilen, journalLog, ergebnis };
};

/**
 * Rechnet die Zuordnung aus dem Journal nach und meldet eine Abweichung.
 *
 * Seit Stufe 3b nur noch im JSON-Modus (Tor-Notschalter AN) und fuer Belege
 * ohne Leistungszeile mit positiver Menge (`anlass`, siehe `SchattenAnlass`):
 * sonst entscheidet das Tor selbst (allocationGate.ts), mit denselben
 * Bausteinen.
 *
 * WIRFT NIE und lehnt nie ab: jeder Fehler — im Lesen, im Planen, im
 * Vergleichen — wird gemeldet und geschluckt. Der Aufrufer ruft ohne `await`
 * (`void schattenAmTor(…).catch(() => {})`); das `catch` dort ist nur der
 * Gurt zum Hosentraeger.
 *
 * Der Teil VOR dem ersten `await` laeuft noch innerhalb von
 * `planProjectIssue` und haelt deshalb den Schnappschuss fest
 * (`schnappschussAmTor`): bis das Journal da ist, ist die Ausstellung laengst
 * weitergelaufen.
 */
export const schattenAmTor = async (
    eingang: SchattenAmTorEingang,
    deps: SchattenAmTorDeps = STANDARD_TOR_DEPS,
    anlass: SchattenAnlass = 'notschalter',
): Promise<void> => {
    const kopf = torKopf(eingang);
    try {
        // ── Schnappschuss, synchron ──────────────────────────────────────
        const schnappschuss = schnappschussAmTor(eingang, kopf);
        // Bei durchgereichten Belegen dieselbe Leer-Erkennung wie am Tor
        // (allocationGate.ts) — hier nur fuer die Meldung, gesperrt wird nie.
        // Im JSON-Modus bleibt es beim Vergleich wie bis Stufe 3a.
        const leistungen = anlass === 'durchgereicht' ? abbildbareLeistungen(schnappschuss.jsonLog) : 0;

        // ── Ab hier asynchron ────────────────────────────────────────────
        const journal = await deps.ladeJournal(kopf.projectId);
        if (!journal) {
            await sicherMelden(deps, nichtLesbarMeldung(kopf, anlass));
            return;
        }
        if (journal.length === 0 && leistungen > 0) {
            await sicherMelden(deps, nichtLesbarMeldung(kopf, anlass, leistungen));
            return;
        }
        const { ergebnis } = journalwegAmTor(schnappschuss, journal, deps.plane);
        if (ergebnis.gleich) return;
        await sicherMelden(deps, abweichungsMeldung(kopf, ergebnis, schattenForm(anlass)));
    } catch (fehler) {
        await sicherMelden(deps, ausfallMeldung(kopf, fehler, anlass));
    }
};
